// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useEffect, useState } from "react";
import { Link as RouterLink, NavLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { AppConfig, roles } from "../../../../configs/admin-config";
import { checkIfRoleExists } from "../../../../utils/oauth";
import MuiAlert from "@mui/material/Alert";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useHttp from "../../../../utils/http";
import {
  Container,
  Typography,
  Paper,
  TextField,
  Backdrop,
  CircularProgress,
  Accordion,
  AccordionSummary,
  Grid,
  AccordionDetails,
  Chip,
  Button,
  Snackbar,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuestionDetails from "./QuestionDetails";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  backgroundColor: {
    backgroundColor: "#F7F7F7",
    minHeight: "100vh",
    marginTop: 48,
  },
  container: {
    flexGrow: 1,
    paddingBottom: 30,
    paddingTop: 30,
  },
  formStatusGrid: {
    marginBottom: 20,
  },
  justifyText: {
    textAlign: "justify",
  },
  accordionSummary: {
    padding: "0% 4%",
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  headingName: {
    textAlign: "justify",
    fontWeight: "bold",
  },
  accordionDetails: {
    padding: "1.5% 4%",
    paddingBottom: 30,
  },
  button: {
    backgroundColor: "white",
    textTransform: "none",
  },
  buttonGrid: {
    textAlign: "end",
  },
}));

const RejectedPage = (props) => {
  const history = useHistory();
  const { classes } = useStyles();
  const { isLoading, data, error, sendRequest, reqExtra, isOpen } = useHttp();

  const [backdropOpen, setbackDrop] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setExpanded] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [subQuestions, setSubQuestions] = useState([]);
  const [successSnack, setSuccess] = useState(false);
  const [copySnack, setCopy] = useState(false);
  const [failSnack, setFail] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [resellerData, setResellerData] = useState([]);
  const [linkData, setLinkData] = useState(null);
  const [formRender, setFormRender] = useState(0);

  const getResellerData = () => {
    const requestURL =
      AppConfig.baseUrl +
      AppConfig.getResellerDataByCompanyId +
      props.match.params.companyId;
    sendRequest(requestURL, "GET", null, AppConfig.getResellerDataByCompanyId);
  };

  const getTradeReferenceData = () => {
    const requestURL =
      AppConfig.baseUrl +
      AppConfig.getTradeReferences +
      props.match.params.companyId +
      "/" +
      props.match.params.linkId;
    sendRequest(requestURL, "GET", null, AppConfig.getTradeReferenceData);
  };

  const getTradeReferenceInfoQuestions = () => {
    const requestURL =
      AppConfig.baseUrl + AppConfig.getTradeReferenceQuestionData;
    sendRequest(
      requestURL,
      "GET",
      null,
      AppConfig.getTradeReferenceQuestionData
    );
  };

  useEffect(() => {
    if (checkIfRoleExists(roles.finance)) {
      setbackDrop(true);
      getResellerData();
    } else {
      history.replace({
        pathname: AppConfig.urls.NO_ACCESS_PAGE,
      });
    }
  }, [formRender]);

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
  });  

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setFail(false);
    setCopy(false);
  };

  useEffect(() => {
    switch (reqExtra) {
      case AppConfig.getResellerDataByCompanyId:
        if (data && data.length > 0) {
          setResellerData(data[0]);
        } else {
          console.warn("Not retrieved any data from backend");
        }
        getTradeReferenceData();
        break;
      case AppConfig.getTradeReferenceData:
        if (data && !error) {
          if (data.data) {
            setAnswers(data.data);
            getTradeReferenceInfoQuestions();
          }
          if (data.linkData) {
            setLinkData(data.linkData[0]);
          }
          setLoading(false);
          setbackDrop(false);
        } else {
          setLoading(false);
          setbackDrop(false);
          console.warn("Not retrieved link data from backend");
        }
        break;
      case AppConfig.getTradeReferenceQuestionData:
        if (data && !error) {
          if (data.questions) {
            setQuestions(data.questions);
          }
          if (data.subQuestions) {
            setSubQuestions(data.subQuestions);
          }
          setLoading(false);
          setbackDrop(false);
        } else if (error) {
          setLoading(false);
          setbackDrop(false);
        }
        break;
      default:
        break;
    }
  }, [data, reqExtra, isOpen, isLoading, error]);

  return (
    <React.Fragment>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>

      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={successSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Success!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={copySnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Copied to Clipboard!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={failSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Server Error!
        </Alert>
      </Snackbar>
      {linkData?.status && (
        <div className={classes.backgroundColor}>
          <Container className={classes.container}>
            <Grid container spacing={1} className={classes.formStatusGrid}>
              <Grid item xs={12}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                  <Grid item>
                    <NavLink to={AppConfig.urls.ADMIN + AppConfig.urls.LANDING_PAGE + AppConfig.urls.TRADE_REFERENCES}>
                      <IconButton aria-label="back">
                        <ArrowBackIcon />
                      </IconButton>
                    </NavLink>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                lg={8}
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Chip
                  variant="outlined"
                  label={
                    "Form Status: Rejected"
                  }
                  color={"primary"}
                />
              </Grid>
            </Grid>

            <div>
              <Paper>
                <Accordion
                  expanded={isExpanded}
                  onChange={() => {
                    setExpanded(!isExpanded);
                  }}
                >
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ExpandMoreIcon style={{ color: "white" }} />}
                  >
                    <Grid item xs={12}>
                      <Typography className={classes.headingName}>Trade Reference Information</Typography>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div>
                          <Typography style={{ textAlign: "justify" }} variant="body2">
                            Regarding
                          </Typography>
                        </div>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          defaultValue={resellerData && resellerData.companyName}
                          disabled
                          size="small"
                          label="Reference Company Name"
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <TextField
                          defaultValue={resellerData && resellerData.country}
                          disabled
                          size="small"
                          label="Country"
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                      {resellerData && resellerData.state !== "" && (
                        <Grid item xs={12} lg={6}>
                          <TextField
                            defaultValue={resellerData && resellerData.state}
                            disabled
                            size="small"
                            label="State"
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} lg={6}>
                        <TextField
                          defaultValue={resellerData && resellerData.cityProvince}
                          disabled
                          size="small"
                          label="City/Province"
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <TextField
                          defaultValue={resellerData && resellerData.streetAddress}
                          disabled
                          size="small"
                          label="Street Address"
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>

                      {questions.length > 0 &&
                        subQuestions.length > 0 &&
                        questions
                          .filter((question) =>
                            [74, 37, 38, 39].includes(question.questionId)
                          )
                          .map((question) => {
                            return (
                              <QuestionDetails
                                subQuestions={subQuestions}
                                question={question}
                                key={question.questionId}
                                answers={answers}
                                formCompleted={true}
                              />
                            );
                          })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </div>
          </Container>
        </div>
      )}
    </React.Fragment>
  );
};

export default RejectedPage;
